import { useEffect, useState } from 'react';
import { getReportFeatureFlags } from '../utilities/api';

/**
 * DISCOUNT_BUTTON / NC toggles from application_setting (setting_access = 1 = enabled),
 * plus the branch's org_name/branch_title — all shared by every report (Bill/Sales
 * Reporta/Sales Report/Item/Category) from one call, so Item/Category (whose own per-tab
 * endpoints don't otherwise touch branch_creation) can still show an organization name in
 * their Excel/PDF/Print export headers. Refetches whenever the selected branch changes,
 * since both are branch-scoped.
 *
 * @param {{branch_id?: string}|string|null} [selectedBranch]
 * @returns {{ discountEnabled: boolean, ncEnabled: boolean, organizationName: string, loading: boolean }}
 */
export default function useReportFeatureFlags(selectedBranch) {
    const [flags, setFlags] = useState({ discountEnabled: false, ncEnabled: false, orgName: '', branchTitle: '' });
    const [loading, setLoading] = useState(true);

    const branchId = (selectedBranch && typeof selectedBranch === 'object' ? selectedBranch.branch_id : selectedBranch) || 'All';

    useEffect(() => {
        let cancelled = false;
        const clientId = localStorage.getItem('clientid') || '';

        const fetchFlags = async () => {
            setLoading(true);
            const result = await getReportFeatureFlags({ branch_id: branchId, client_id: clientId });
            if (!cancelled) {
                setFlags({
                    discountEnabled: !!result.discount_enabled,
                    ncEnabled: !!result.nc_enabled,
                    orgName: result.org_name || '',
                    branchTitle: result.branch_title || ''
                });
                setLoading(false);
            }
        };
        fetchFlags();

        return () => {
            cancelled = true;
        };
    }, [branchId]);

    // Same org_name / branch_title precedence every report's own export header uses. Both
    // are trimmed first — a branch_title that's DB whitespace-only (truthy but blank) would
    // otherwise still pass the "set and different" check and print a dangling " / " with
    // nothing after it.
    const orgNameTrimmed = flags.orgName.trim();
    const branchTitleTrimmed = flags.branchTitle.trim();
    const organizationName =
        orgNameTrimmed && branchTitleTrimmed && orgNameTrimmed !== branchTitleTrimmed ? `${orgNameTrimmed} / ${branchTitleTrimmed}` : orgNameTrimmed || branchTitleTrimmed || '';

    return { discountEnabled: flags.discountEnabled, ncEnabled: flags.ncEnabled, organizationName, loading };
}
